import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Lightbox } from "./Lightbox";

const ZoomFrame = ({ item, onOpen }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <div className="absolute inset-0 cursor-zoom-in overflow-hidden" style={{ background: item.fit === "flush" ? "#EFE9E1" : "#fff" }} data-testid="pdp-zoom-frame" onClick={onOpen}
      onPointerEnter={(e) => e.pointerType === "mouse" && setZoom(true)} onPointerLeave={() => setZoom(false)} onPointerMove={onMove}>
      <AnimatePresence initial={false}>
        <motion.img key={item.src} src={item.src} alt={item.label} draggable={false}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
          style={{ transformOrigin: origin }}
          className={`absolute inset-0 h-full w-full transition-transform duration-300 ease-out ${item.fit === "contain" ? "object-contain p-8" : item.fit === "flush" ? "object-contain" : "object-cover"} ${zoom ? "scale-[2.2]" : "scale-100"}`} />
      </AnimatePresence>
    </div>
  );
};

export const Gallery = ({ items, idx, setIdx, colorName }) => {
  const [open, setOpen] = useState(false);
  const go = (d) => setIdx((idx + d + items.length) % items.length);
  const item = items[idx];
  return (
    <div className="flex flex-col-reverse gap-3 lg:flex-row" data-testid="pdp-gallery">
      <div className="flex gap-2 overflow-x-auto lg:max-h-[78vh] lg:w-20 lg:flex-col lg:overflow-y-auto" data-lenis-prevent>
        {items.map((it, k) => (
          <button key={it.src + k} onClick={() => setIdx(k)} aria-label={it.label} data-testid={`pdp-thumb-${k}`}
            className={`relative h-20 w-16 shrink-0 overflow-hidden bg-white ring-1 transition-[box-shadow,opacity] lg:w-20 ${k === idx ? "ring-ink" : "opacity-70 ring-line hover:opacity-100"}`}>
            <img src={it.src} alt="" loading="lazy" className={`h-full w-full ${it.fit === "cover" ? "object-cover" : "object-contain p-1"}`} />
          </button>
        ))}
      </div>
      <motion.div className="relative aspect-square flex-1 touch-pan-y overflow-hidden bg-white" drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.15}
        onDragEnd={(_, i) => { if (i.offset.x < -60) go(1); if (i.offset.x > 60) go(-1); }} data-testid="pdp-main-image">
        <ZoomFrame item={item} onOpen={() => setOpen(true)} />
        <div className="pointer-events-none absolute left-4 top-4 bg-ivory/90 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
          {item.label}{item.shared && colorName !== "Beige" ? " · shown in Beige" : ""}
        </div>
        <button onClick={() => go(-1)} aria-label="Previous image" data-testid="pdp-prev-image" className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-ivory/90 text-ink"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={() => go(1)} aria-label="Next image" data-testid="pdp-next-image" className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-ivory/90 text-ink"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => setOpen(true)} aria-label="Full screen" data-testid="pdp-open-lightbox" className="absolute bottom-4 right-4 grid h-10 w-10 place-items-center rounded-full bg-ivory/90 text-ink"><Expand className="h-4 w-4" /></button>
        <span className="absolute bottom-5 left-4 font-mono text-xs text-graphite">{idx + 1} / {items.length}</span>
      </motion.div>
      <Lightbox open={open} onOpenChange={setOpen} items={items} idx={idx} go={go} />
    </div>
  );
};
